import { useEffect } from 'react';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import { NotFound } from './components/not-found';
import { initAnalytics } from './lib/analytics';
import { initFirstPromoter } from './lib/firstpromoter';
import { initOpenAIPixel } from './lib/openai-pixel';
import './styles/globals.css';

// Phase 129 Plan 02 Task 1 + Plan 04 Task 1 — root layout for web/.
// Dark mode only per CONTEXT.md (DESIGN.md tokens applied via Tailwind v4
// @theme).
//
// Sentry init lives in entry.client.tsx (browser) and instrument.server.mjs
// (SSR runtime, preloaded via NODE_OPTIONS='--import …'). Both feed the
// shared hookmyapp-web Sentry project. PostHog init runs in the App effect
// below so it kicks off after hydration.

// AIT-117: default title/description for every route without its own meta
// export. Brand-first title so the Google brand-query result reads well.
// AIT-268: og:* + twitter:card defaults so shared links render a card. Routes
// with their own meta() REPLACE this array and must repeat these descriptors.
export function meta() {
  const title = 'HookMyApp: AI Access to WhatsApp and Instagram';
  const description =
    'Give your AI access to WhatsApp and Instagram and start messaging free in 2 minutes. Your AI coding agent sets everything up for you.';
  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: 'https://hookmyapp.com/og-default.png' },
    { property: 'og:url', content: 'https://hookmyapp.com/' },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ];
}

// AIT-117: Organization structured data for the brand result / knowledge
// panel. Static, so it's safe to render on every page.
const ORGANIZATION_JSONLD = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'HookMyApp',
  url: 'https://hookmyapp.com',
  logo: 'https://hookmyapp.com/logo-mark.svg',
  sameAs: ['https://github.com/hookmyapp', 'https://www.npmjs.com/package/@gethookmyapp/cli'],
});

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        {/* DESIGN.md cornerstone typeface — Optimistic VF is paid; Montserrat
            is the prescribed open-source fallback. Loaded as a variable font
            so all weights (100–900) are available without separate requests. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap"
        />
        <Meta />
        <Links />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ORGANIZATION_JSONLD }} />
      </head>
      <body className="bg-[#1C1E21] text-white antialiased font-sans">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  useEffect(() => {
    initAnalytics();
    initFirstPromoter();
    initOpenAIPixel();
  }, []);
  return <Outlet />;
}

// AIT-355 — branded error boundary. 404 route errors render the shared
// NotFound (same page the splat route serves); anything else gets a minimal
// branded error state. Server Sentry instrumentation already captures the
// underlying error — no capture calls here.
export function ErrorBoundary({ error }: { error: unknown }) {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }
  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <h1 className="text-4xl font-medium tracking-tight text-white">Something went wrong</h1>
      <p className="mt-4 max-w-md text-lg text-white/65">
        An unexpected error occurred. Try again in a moment.
      </p>
      <a
        href="/"
        className="mt-10 inline-flex items-center justify-center rounded-[100px] bg-[#0064E0] px-8 py-4 text-base font-medium text-white transition hover:bg-[#0143B5]"
      >
        Back to home
      </a>
    </main>
  );
}
